// src/components/Workbench/components/EtlUnit/DerivedFieldConfig.jsx

import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import PropTypes from 'prop-types';
import clsx from 'clsx';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import SelectMenu from '../../../shared/SelectMenu';

import {
  addDerivedField,
  setDerivedFieldRegistry,
} from '../../../../ducks/actions/workbench.actions';
import { selectMaybeDerivedFieldConfig as selectConfig } from '../../../../ducks/rootSelectors';
import { getDerivedFieldRegistry } from '../../../../ducks/workbench.reducer';
import { fetchDerivedFieldRegistry } from '../../../../services/api';

const DEFAULT_INSTRUCTIONS =
  'Apply the selected function to each in the group of fields generated by this request.';

/**
 * @component
 * Derived field configuration panel.
 * Menu options are fetched from the backend registry (/v1/derived-fields).
 */
function DerivedFieldConfig({ nodeId, children }) {
  const dispatch = useDispatch();

  // Registry from Redux store (fetched from backend)
  const registry = useSelector(
    (state) => getDerivedFieldRegistry(state.workbench),
    shallowEqual,
  );

  // Fetch registry on first mount if not already loaded
  useEffect(() => {
    if (registry) return;

    fetchDerivedFieldRegistry()
      .then((data) => {
        dispatch(setDerivedFieldRegistry(data));
      })
      .catch((err) => {
        console.warn('Failed to fetch derived field registry:', err);
      });
  }, [registry, dispatch]);

  // Build menu options from registry
  const menuOptions = (registry || []).map(({ menuItem, id }) => ({
    value: id,
    option: menuItem,
  }));

  // Current selection for this node
  const configName = useSelector(
    (state) => selectConfig(state, nodeId),
    shallowEqual,
  );

  // Find the selected entry in the registry
  const selectedField = (registry || []).find((f) => f.id === configName);

  const handleChange = useCallback(
    ({ target: { value } }) => {
      dispatch(
        addDerivedField({
          identifier: value,
          id: nodeId,
        }),
      );
    },
    [dispatch, nodeId],
  );

  return (
    <Grid container className={clsx('Luci-Workbench-Instructions')}>
      <Grid item xs={4}>
        <SelectMenu
          options={menuOptions}
          name='function'
          value={configName}
          label='function'
          placeholder='function'
          onChange={handleChange}
          className='derivedField'
        />
      </Grid>
      <Grid item xs={7}>
        <Typography display='inline'>
          {selectedField?.instructions || DEFAULT_INSTRUCTIONS}
        </Typography>
      </Grid>
      {children}
    </Grid>
  );
}
DerivedFieldConfig.propTypes = {
  nodeId: PropTypes.number.isRequired,
  children: PropTypes.node,
};
DerivedFieldConfig.defaultProps = { children: null };

export default DerivedFieldConfig;
